import { z } from 'zod';
import { COMMITTEE_ROLES } from './enums';
import { criterionSchema } from './registry';
import { idSchema, optionalText } from './schemas';

// ───────────── Правило 12.4.2: бал і рейтинг кандидата ─────────────

export interface Criterion {
  id: string;
  maxScore: number;
  weight: number;
  minScore: number | null;
}

/** Оцінки одного члена комісії: criterionId → бал. */
export type Scores = Record<string, number>;

/**
 * Бал члена комісії у відсотках 0..100: Σ(бал_i / макс_i × вага_i) / Σ вага_i × 100.
 * null — якщо оцінено не всі критерії.
 */
export function evaluatorScore(criteria: Criterion[], scores: Scores): number | null {
  if (criteria.length === 0) return null;
  let sum = 0;
  let weights = 0;
  for (const c of criteria) {
    const s = scores[c.id];
    if (s === undefined) return null;
    sum += (Math.min(Math.max(s, 0), c.maxScore) / c.maxScore) * c.weight;
    weights += c.weight;
  }
  return weights === 0 ? null : round2((sum / weights) * 100);
}

export interface CandidateAggregate {
  /** Підсумковий бал — середнє балів членів комісії, які завершили оцінку. */
  total: number | null;
  /** Середній бал за кожним критерієм (у балах шкали критерію). */
  perCriterion: Record<string, number | null>;
  /** Скільки членів комісії завершили оцінку. */
  evaluations: number;
  /** Середній бал хоча б одного критерію нижче мінімального. */
  belowMinimum: boolean;
  failedCriteria: string[];
}

export function aggregateCandidate(criteria: Criterion[], submitted: Scores[]): CandidateAggregate {
  const totals = submitted
    .map((s) => evaluatorScore(criteria, s))
    .filter((v): v is number => v !== null);
  const perCriterion: Record<string, number | null> = {};
  const failedCriteria: string[] = [];
  for (const c of criteria) {
    const values = submitted.map((s) => s[c.id]).filter((v): v is number => v !== undefined);
    const avg = values.length ? round2(values.reduce((a, b) => a + b, 0) / values.length) : null;
    perCriterion[c.id] = avg;
    if (c.minScore !== null && avg !== null && avg < c.minScore) failedCriteria.push(c.id);
  }
  return {
    total: totals.length ? round2(totals.reduce((a, b) => a + b, 0) / totals.length) : null,
    perCriterion,
    evaluations: totals.length,
    belowMinimum: failedCriteria.length > 0,
    failedCriteria,
  };
}

/**
 * Рейтинг за спаданням балу; рівні бали ділять місце (1, 1, 3).
 * Кандидати без балу місця не мають; ті, хто не пройшов мінімум, ідуть після решти.
 */
export function rankCandidates(
  items: { id: string; total: number | null; belowMinimum: boolean }[],
): Map<string, number> {
  const ranked = items
    .filter((i): i is { id: string; total: number; belowMinimum: boolean } => i.total !== null)
    .sort((a, b) => Number(a.belowMinimum) - Number(b.belowMinimum) || b.total - a.total);
  const result = new Map<string, number>();
  ranked.forEach((item, index) => {
    const prev = ranked[index - 1];
    const same = prev && prev.total === item.total && prev.belowMinimum === item.belowMinimum;
    result.set(item.id, same ? result.get(prev.id)! : index + 1);
  });
  return result;
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

// ───────────── Схеми ─────────────

export const contestCriteriaSetSchema = z.object({
  contestId: idSchema,
  criteria: z.array(criterionSchema.and(z.object({ id: idSchema.optional() }))).max(30),
});

export const committeeSetSchema = z.object({
  contestId: idSchema,
  members: z
    .array(z.object({ userId: idSchema, role: z.enum(COMMITTEE_ROLES) }))
    .max(20)
    .refine((m) => new Set(m.map((x) => x.userId)).size === m.length, 'Користувач двічі в комісії')
    .refine(
      (m) => m.filter((x) => x.role === 'chair').length <= 1,
      'Голова комісії може бути один',
    ),
});

export const evaluationSaveSchema = z.object({
  applicationId: idSchema,
  scores: z
    .array(
      z.object({
        criterionId: idSchema,
        score: z.number().int().min(0).max(100),
        comment: optionalText(1000),
      }),
    )
    .max(30),
  comment: optionalText(3000),
  /** true — завершити оцінку (стає видимою в рейтингу). */
  submit: z.boolean().default(false),
});
export type EvaluationSaveInput = z.input<typeof evaluationSaveSchema>;
